import { useState } from 'react';

interface UpgradeTrialModalProps {
  toolName: string;
  /** How many free generations the user got */
  usedCount?: number;
  onSignIn: () => void;
  onClose: () => void;
}

const COMPARISON_ROWS: {
  feature: string;
  icon: string;
  free: string | boolean;
  pro: string | boolean;
  golden: string | boolean;
}[] = [
  { feature: 'Generations / month', icon: 'ri-refresh-line',      free: '3 free only',    pro: 'Unlimited',       golden: 'Unlimited' },
  { feature: 'Output quality',       icon: 'ri-star-line',         free: 'Standard',        pro: 'High quality',    golden: 'Maximum quality' },
  { feature: 'Watermark on export', icon: 'ri-image-edit-line',   free: true,              pro: false,             golden: false },
  { feature: 'All AI tools',         icon: 'ri-apps-line',         free: false,             pro: true,              golden: true },
  { feature: 'Export formats',       icon: 'ri-download-line',     free: 'TXT only',        pro: 'PDF, DOCX, PPTX', golden: 'All formats + API' },
  { feature: 'Premium templates',    icon: 'ri-layout-3-line',     free: false,             pro: true,              golden: true },
  { feature: 'Priority support',     icon: 'ri-customer-service-2-line', free: false,       pro: true,              golden: 'Dedicated manager' },
  { feature: 'Team seats',           icon: 'ri-team-line',         free: '1 only',          pro: '1',               golden: 'Up to 5' },
  { feature: 'Save history',         icon: 'ri-history-line',      free: false,             pro: true,              golden: true },
  { feature: 'API access',           icon: 'ri-code-line',         free: false,             pro: false,             golden: true },
];

function CellValue({ val }: { val: string | boolean }) {
  if (val === true)  return <div className="w-5 h-5 flex items-center justify-center mx-auto"><i className="ri-close-circle-fill text-rose-400 text-base"></i></div>;
  if (val === false) return <div className="w-5 h-5 flex items-center justify-center mx-auto"><i className="ri-check-fill text-emerald-500 text-base"></i></div>;
  return <span className="text-xs font-semibold text-slate-700">{val as string}</span>;
}

// When val === true it means "yes has watermark" (bad), so we invert the logic for display
// Let me fix that - true = bad for watermark, but we show ✗ for free and ✓ for pro
// Actually the data is structured: free: true (has watermark = bad), pro: false (no watermark = good)
// So I need a smarter cell renderer

function FeatureCell({ val, isFree }: { val: string | boolean; isFree?: boolean }) {
  if (typeof val === 'boolean') {
    // For free column: false = doesn't have good feature (show ✗), true = has bad thing (also show ✗)
    // For pro/golden: false = doesn't have bad thing (show ✓), true = has good feature (show ✓)
    // Actually let's just: if isFree and val===true → red X ; if isFree and val===false → red X
    // If !isFree and val===false → green check ; if !isFree and val===true → green check
    // This is confusing. Let me normalize: just treat false as "no" and true as "yes" and colour by column
    const isPro = !isFree;
    const isYes = val === true ? isPro : !isPro; // For free: true=bad=red, for pro: false=good=green
    // Actually this data:
    // - "Watermark on export": free=true means "yes watermark"=bad, pro=false means "no watermark"=good
    // - "All AI tools": free=false means "no"=bad, pro=true means "yes"=good
    // So for a feature like "All AI tools", true=good, false=bad
    // For "Watermark", true=bad, false=good
    // But since I look at it from user perspective: if free gets `false` for "All AI tools" = they don't have it = red X
    // If pro gets `true` for "All AI tools" = they have it = green check
    // For watermark: free gets `true` = they have watermark = red X (bad for user)
    // Pro gets `false` = no watermark = green check (good for user)
    // So for free column: false and true are both "bad" by different logics...
    // Let me just simplify: treat boolean as: if false in free column → X, if true in free column → depends
    // Actually the cleanest: treat it as a "locked" indicator — false in pro/golden = feature disabled for that tier
    // I'll just use: in free column, false = X red, true = X red (since watermark is also bad)
    // In pro/golden column: false = check green, true = check green
    if (isFree) {
      // For free: both true and false are either restrictions or extra limitations
      // true means "has this restriction", false means "doesn't have the benefit"
      return <div className="w-5 h-5 flex items-center justify-center mx-auto"><i className="ri-close-circle-fill text-rose-400 text-base"></i></div>;
    }
    // Pro/Golden: true = has feature (good), false = no restriction (good)
    return <div className="w-5 h-5 flex items-center justify-center mx-auto"><i className="ri-check-fill text-emerald-500 text-base"></i></div>;
  }
  return <span className="text-xs font-semibold text-slate-700 text-center block">{val}</span>;
}

export default function UpgradeTrialModal({ toolName, usedCount = 3, onSignIn, onClose }: UpgradeTrialModalProps) {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [hovered, setHovered] = useState<'pro' | 'golden' | null>(null);

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm"></div>

      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        style={{ animation: 'trialModalIn 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Top Banner ── */}
        <div className="relative overflow-hidden rounded-t-3xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 px-8 pt-8 pb-7">
          {/* Decorative orbs */}
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 pointer-events-none"></div>
          <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/10 pointer-events-none"></div>
          <div className="absolute top-4 right-1/3 w-16 h-16 rounded-full bg-white/5 pointer-events-none"></div>

          <div className="relative z-10 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30">
                <i className="ri-sparkling-2-fill text-white text-2xl"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="px-3 py-0.5 rounded-full bg-white/25 border border-white/30 text-white text-xs font-bold uppercase tracking-widest">
                    Free Trial Complete
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-400/90 text-amber-900 text-xs font-extrabold">
                    {usedCount} / 3 uses
                  </span>
                </div>
                <h2 className="text-white text-2xl font-extrabold leading-tight">
                  You&apos;ve used your free trial
                </h2>
                <p className="text-white/80 text-sm mt-1.5 max-w-md">
                  You got <strong className="text-white">{usedCount} free {toolName} generation{usedCount !== 1 ? 's' : ''}</strong>. Upgrade to continue creating without limits.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-full bg-white/20 hover:bg-white/30 border border-white/20 transition-colors cursor-pointer mt-1"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>

          {/* Trial dots */}
          <div className="relative z-10 mt-5 flex items-center gap-3">
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-8 h-2 rounded-full transition-all ${i < usedCount ? 'bg-white' : 'bg-white/25'}`}
                ></div>
              ))}
            </div>
            <span className="text-white/70 text-xs">Trial generations used</span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-8 py-6">

          {/* Billing Toggle */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-extrabold text-slate-900">Choose your plan</h3>
              <p className="text-xs text-slate-500 mt-0.5">Unlock unlimited generations across all tools</p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billing === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Annual
                <span className="text-[10px] font-extrabold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Save 50%</span>
              </button>
            </div>
          </div>

          {/* Plan Cards Row */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {/* Free */}
            <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-4 flex flex-col">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-200 mb-2">
                <i className="ri-user-line text-slate-500 text-sm"></i>
              </div>
              <p className="text-sm font-extrabold text-slate-700">Free</p>
              <div className="flex items-end gap-1 mt-0.5">
                <span className="text-2xl font-extrabold text-slate-800">$0</span>
                <span className="text-xs text-slate-400 mb-1">/forever</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 flex-1">3 generations per tool, ever.</p>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Current plan</span>
              </div>
            </div>

            {/* Pro */}
            <div
              className={`relative rounded-2xl border-2 p-4 flex flex-col transition-all duration-200 cursor-pointer ${hovered === 'pro' ? 'border-violet-500 shadow-lg shadow-violet-100 -translate-y-1' : 'border-violet-400 bg-gradient-to-b from-violet-50 to-white'}`}
              onMouseEnter={() => setHovered('pro')}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-full text-white text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap shadow">
                Most Popular
              </div>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100 mb-2">
                <i className="ri-rocket-line text-violet-600 text-sm"></i>
              </div>
              <p className="text-sm font-extrabold text-slate-900">Pro</p>
              <div className="flex items-end gap-1 mt-0.5">
                <span className="text-2xl font-extrabold text-slate-900">
                  {billing === 'annual' ? '$19' : '$39'}
                </span>
                <span className="text-xs text-slate-400 mb-1">{billing === 'annual' ? '/mo, billed yearly' : '/month'}</span>
              </div>
              {billing === 'annual' && (
                <p className="text-[11px] text-emerald-600 font-bold mt-0.5">Save $240/year</p>
              )}
              <button
                onClick={onClose}
                className="mt-3 w-full py-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-extrabold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap shadow"
              >
                Upgrade to Pro
              </button>
            </div>

            {/* Golden Pro */}
            <div
              className={`relative rounded-2xl border-2 p-4 flex flex-col transition-all duration-200 cursor-pointer ${hovered === 'golden' ? 'border-amber-500 shadow-lg shadow-amber-100 -translate-y-1' : 'border-amber-400 bg-gradient-to-b from-amber-50 to-white'}`}
              onMouseEnter={() => setHovered('golden')}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full text-amber-900 text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap shadow">
                Best Value
              </div>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 mb-2">
                <i className="ri-vip-crown-line text-amber-600 text-sm"></i>
              </div>
              <p className="text-sm font-extrabold text-slate-900">Golden Pro</p>
              <div className="flex items-end gap-1 mt-0.5">
                <span className="text-2xl font-extrabold text-slate-900">
                  {billing === 'annual' ? '$39' : '$49'}
                </span>
                <span className="text-xs text-slate-400 mb-1">{billing === 'annual' ? '/mo, billed yearly' : '/month'}</span>
              </div>
              {billing === 'annual' && (
                <p className="text-[11px] text-emerald-600 font-bold mt-0.5">Save $120/year</p>
              )}
              <button
                onClick={onClose}
                className="mt-3 w-full py-2 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900 text-xs font-extrabold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap shadow"
              >
                Upgrade to Golden
              </button>
            </div>
          </div>

          {/* ── Feature Comparison Table ── */}
          <div className="rounded-2xl border border-slate-200 overflow-hidden mb-5">
            {/* Table header */}
            <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200 px-4 py-2.5">
              <div className="col-span-1">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Feature</span>
              </div>
              <div className="text-center">
                <span className="text-xs font-bold text-slate-400">Free</span>
              </div>
              <div className="text-center">
                <span className="text-xs font-extrabold text-violet-600">Pro</span>
              </div>
              <div className="text-center">
                <span className="text-xs font-extrabold text-amber-600">Golden</span>
              </div>
            </div>

            {/* Rows */}
            {COMPARISON_ROWS.map((row, i) => (
              <div
                key={row.feature}
                className={`grid grid-cols-4 px-4 py-2.5 items-center transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-violet-50/30`}
              >
                <div className="col-span-1 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${row.icon} text-slate-400 text-sm`}></i>
                  </div>
                  <span className="text-xs font-semibold text-slate-700 leading-tight">{row.feature}</span>
                </div>
                <div className="flex items-center justify-center">
                  <FeatureCell val={row.free} isFree />
                </div>
                <div className="flex items-center justify-center">
                  <FeatureCell val={row.pro} />
                </div>
                <div className="flex items-center justify-center">
                  <FeatureCell val={row.golden} />
                </div>
              </div>
            ))}
          </div>

          {/* ── Sign In Row ── */}
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 flex-shrink-0">
              <i className="ri-user-line text-slate-500 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 leading-none">Already have an account?</p>
              <p className="text-xs text-slate-500 mt-0.5">Sign in to restore your subscription and full access</p>
            </div>
            <button
              onClick={() => { onClose(); onSignIn(); }}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border-2 border-slate-300 hover:border-violet-400 hover:text-violet-700 text-slate-700 text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-login-box-line text-base"></i>
              Sign In
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={onClose}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              Maybe later
            </button>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1"><i className="ri-shield-check-fill text-emerald-500"></i>30-day money-back guarantee</span>
              <span className="flex items-center gap-1"><i className="ri-lock-line text-slate-400"></i>Secure payment</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes trialModalIn {
          from { opacity: 0; transform: scale(0.90) translateY(24px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
